import { george, i18n, NetbankingAPI, pr, theme } from "aaa";
import { ui } from "components/util/uiHelpers";
import { FormattedHTMLMessage } from "i18n/util";
import { observer } from "mobx-react";
import { NetbankingAPIClient } from "network/APIClient";
import { parse } from "query-string";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import externalInterfaceStore, { IDeeplinkState } from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import trackingStore from "stores/TrackingStore";
import { IOffer } from "types/offer";
import { safeWindowOpen } from "util/helpers";
import { KAPITALSCHANGE, SavingsOptionsStore, SMARTSCHANGE } from "./SavingsOptionsStore";
import { SavingsOptionsActionCard } from "./components/SavingsOptionsActionCard";
import { SavingsOptionsAppointmentCard } from "./components/SavingsOptionsAppointmentCard";
import { SavingsOptionsInfoBox } from "./components/SavingsOptionsInfoBox";
import { performStoreHandoverTokenExchange } from "util/storeHandover";

type IProps = RouteComponentProps<{ id: string }> & i18n.InjectedIntlProps & {
    store: SavingsOptionsStore;
};

const getHandoverUrl = (offer?: IOffer) => {
    let initialHandoverUrl = offer?.handover?.url;

    if (!initialHandoverUrl) {
        return;
    }

    // Create a copy of the URL and remove productCode and conditionGroup from query params
    let trimmedUrl = new URL(initialHandoverUrl);
    trimmedUrl.searchParams.delete('productCode');
    trimmedUrl.searchParams.delete('conditionGroup');

    const stateObject: IDeeplinkState = {
        srcClientId: externalInterfaceStore.clientId,
        returnState: "/overview",
        redirectUri: externalInterfaceStore.georgeBaseUrl + "/?at=c"
    };

    // add channelProduct, accountId and conditionChange flag to handover url
    const accountId = externalInterfaceStore.accountId;
    if (accountId) {
        trimmedUrl.searchParams.set('channelProduct', offer?.channelProduct || '');
        trimmedUrl.searchParams.set('accountId', accountId);
        trimmedUrl.searchParams.set('conditionChange', 'true');
    }

    // Base64 encode the state object -> same as in AssetCalculation / BSPOptionsOverview
    return `${trimmedUrl.toString()}${trimmedUrl.searchParams.toString() ? "&" : "?"}state=${btoa(JSON.stringify(stateObject))}`;
};

export const SavingsOptionsOverview = observer(({ store }: IProps) => {

    const handleHandover = (selectedOffer?: IOffer) => {
        console.log("[SavingsOptionsOverview] handleHandover selectedOffer:", selectedOffer);
        const url = getHandoverUrl(selectedOffer);
        if (!url) {
            pushRoute("/error");
            return;
        }

        performStoreHandoverTokenExchange(url);
    };

    const handleClickCapitalSavings = () => {
        trackingStore.trackEvent({ eventName: "savingsOptionsOverviewScreen", productKey: KAPITALSCHANGE });
        handleHandover(store.capitalSavingsOffer);
    };

    const handleClickSmartSaving = () => {
        trackingStore.trackEvent({ eventName: "savingsOptionsOverviewScreen", productKey: SMARTSCHANGE });
        handleHandover(store.smartSavingsOffer);
    };

    const BackgroundComponent = externalInterfaceStore.isGeorgeApp ? george.Background : george.BackgroundWithNavBar;

    if (store.isLoading) {
        return (
            <BackgroundComponent>
                <george.LoadingIndicator isLoading={store.isLoading} height={ui.windowHeight} />
            </BackgroundComponent>
        );
    }

    const isMobile = externalInterfaceStore.isGeorgeApp || ui.windowWidth < 600;
    const hasContractOptions = store.canChooseCapitalSavings
        || store.canChooseSmartSavings
        || store.canBookAppointment;
    const proposeInstituteChange = store.proposeInstituteChange;

    const smartsavingImage = require("../../assets/kapitalsparen_256832888.jpg");
    const capitalsavingImage = require("../../assets/smart-sparen-hero-mobil.jpg");

    return (
        <BackgroundComponent style={{ paddingTop: 48, paddingBottom: theme.MARGIN }}>
            {store.savingsAccount && hasContractOptions ? (
                <>
                    <div style={pr.STYLE_CENTERED_CARD}>
                        <pr.P_22_TITLE style={{ fontWeight: 700, lineHeight: "28px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                            {ui.__("savingsOptions.intro.title")}
                        </pr.P_22_TITLE>
                        <pr.P_16_CONTENT style={{ lineHeight: "24px", color: ui.isDarkMode ? "white" : "#0A285C" }}>
                            <FormattedHTMLMessage
                                id="savingsOptions.intro.changedate.available"
                                values={{
                                    terminationDate: store.terminationDate ? store.terminationDate.toLocaleDateString("de-DE", {
                                        day: "2-digit",
                                        month: "2-digit",
                                        year: "numeric"
                                    }) : ""
                                }}
                            />
                        </pr.P_16_CONTENT>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 32 }}>
                        {store.canChooseCapitalSavings && (
                            <SavingsOptionsActionCard
                                isMobile={isMobile}
                                title={ui.__("savingsOptions.card.capitalsaving.title")}
                                content={(
                                    <pr.P_14_CONTENT>
                                        <FormattedHTMLMessage
                                            id="savingsOptions.card.capitalsaving.text"
                                            values={{
                                                interestRate: store.capitalSavingsInterestRate || "",
                                                duration: store.capitalSavingsDuration || ""
                                            }}
                                        />
                                    </pr.P_14_CONTENT>
                                )}
                                image={smartsavingImage}
                                actionButtonText={ui.__("savingsOptions.card.capitalsaving.button")}
                                onClickActionButton={handleClickCapitalSavings}
                            />
                        )}
                        {store.canChooseSmartSavings && (
                            <SavingsOptionsActionCard
                                isMobile={isMobile}
                                title={ui.__("savingsOptions.card.smartsaving.title")}
                                content={(
                                    <pr.P_14_CONTENT>
                                        <FormattedHTMLMessage
                                            id="savingsOptions.card.smartsaving.text"
                                            values={{
                                                interestRate: store.smartSavingsInterestRate || "",
                                            }}
                                        />
                                    </pr.P_14_CONTENT>
                                )}
                                image={capitalsavingImage}
                                actionButtonText={ui.__("savingsOptions.card.smartsaving.button")}
                                onClickActionButton={handleClickSmartSaving}
                            />
                        )}
                    </div>
                    {store.canBookAppointment && (
                        <SavingsOptionsAppointmentCard isMobile={isMobile} style={{ marginTop: 32 }} />
                    )}
                    <SavingsOptionsInfoBox style={{ marginTop: 16 }} >
                        <pr.P_16_CONTENT>
                            {ui.__("savingsOptions.overview.infoBox.withOptions")}
                        </pr.P_16_CONTENT>
                    </SavingsOptionsInfoBox>
                </>
            ) : (
                <SavingsOptionsInfoBox>
                    <pr.P_16_CONTENT>
                        {proposeInstituteChange
                            ? ui.__("savingsOptions.overview.infoBox.noOptions")
                            : ui.__("savingsOptions.overview.infoBox.noDetails")
                        }
                    </pr.P_16_CONTENT>
                </SavingsOptionsInfoBox>
            )}
        </BackgroundComponent>
    );
});



// WEBPACK FOOTER //
// ./src/components/savingsOptions/SavingsOptionsOverview.tsx